import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Common";
import { previewDocument } from "@/pages/Documenti";
import {
  STATO_CORSO, MACRO_TIPOLOGIA, STATO_ISCRIZIONE, PRIORITA, STATO_TASK,
  fmtDate, fmtDateTime, fmtEuro,
} from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, Clock, Users, Presentation, CalendarDays, ClipboardCheck,
  FolderArchive, Euro, ListChecks, History, Building2, AlertTriangle, Lock,
  Plus, Trash2, Eye, Upload, Landmark, Pencil, UploadCloud, MapPin, UserPlus,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-1 font-display text-xl font-bold text-slate-900">{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-slate-400" data-testid={`stat-hint-${label.toLowerCase()}`}>{hint}</div>}
    </div>
  );
}

// Conditional funding field config per macro-tipologia (spec §8.8 / §8.10 / §8.12 / §8.13)
const FIN_FIELDS = {
  fse_gol: [
    { name: "codice_progetto", label: "Codice Progetto" },
    { name: "cup", label: "Codice CUP" },
    { name: "decreto_approvazione_num", label: "Decreto Approvazione n." },
    { name: "decreto_approvazione_data", label: "Data Approvazione", type: "date" },
    { name: "decreto_concessione_num", label: "Decreto Concessione n." },
    { name: "decreto_concessione_data", label: "Data Concessione", type: "date" },
    { name: "termine_chiusura", label: "Termine Chiusura Operazioni", type: "date" },
    { name: "proroga_termini", label: "Proroga Termini", type: "date" },
    { name: "contributo", label: "Contributo (€)", type: "number" },
    { name: "programma_specifico", label: "Programma Specifico", type: "combobox", optionKey: "programma_specifico" },
  ],
  fondi_interprofessionali: [
    { name: "fondo", label: "Fondo", type: "select", options: ["Formazienda", "Fondimpresa", "Fondirigenti", "Fondartigianato", "Altro"] },
    { name: "canale", label: "Canale", type: "select", options: ["CFI", "Avviso", "Contributo aggiuntivo", "Altro"] },
    { name: "codice_azienda_fondo", label: "Codice Azienda Fondo" },
    { name: "avviso", label: "Avviso / Invito" },
    { name: "azienda_beneficiaria", label: "Azienda beneficiaria" },
    { name: "titolo_piano", label: "Titolo Piano Formativo" },
    { name: "id_piano", label: "ID Piano Formativo" },
    { name: "cup", label: "CUP" },
    { name: "titolo_progetto", label: "Titolo Progetto / Azione" },
    { name: "data_approvazione", label: "Data Approvazione", type: "date" },
    { name: "scadenza_monitoraggio", label: "Scadenza Monitoraggio", type: "date" },
    { name: "scadenza_rendiconto", label: "Scadenza Rendiconto", type: "date" },
    { name: "importo_contratto_indar", label: "Importo Contratto Indar (€)", type: "number" },
    { name: "finanziamento_approvato", label: "Finanziamento Approvato (€)", type: "number" },
    { name: "finanziamento_rendicontato", label: "Finanziamento Rendicontato (€)", type: "number" },
  ],
  treu: [
    { name: "titolo", label: "Titolo Pratica TREU" },
    { name: "data_inizio", label: "Data Inizio", type: "date" },
    { name: "data_fine", label: "Data Fine", type: "date" },
    { name: "data_proroga", label: "Data Proroga", type: "date" },
    { name: "data_cessazione", label: "Data Cessazione Anticipata", type: "date" },
    { name: "azienda_ospitante", label: "Azienda Ospitante" },
  ],
  non_finanziato_mono: [
    { name: "codice_preventivo", label: "Codice Preventivo" },
    { name: "valore_preventivo", label: "Valore Preventivo (€)", type: "number" },
  ],
  non_finanziato_pluri: [
    { name: "codice_preventivo", label: "Codice Preventivo" },
    { name: "quota_partecipante", label: "Quota Partecipante (€)", type: "number" },
  ],
};

export default function CorsoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [persone, setPersone] = useState([]);
  const [docenti, setDocenti] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [personale, setPersonale] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [qc, setQc] = useState(null); // quick-create anagrafica {type, form}
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [opts, setOpts] = useState({});
  const [sessConf, setSessConf] = useState([]);

  useEffect(() => {
    const sedeChk = form.sede && form.modalita !== "online" ? form.sede : "";
    if (dialog !== "sessione" || form.stato === "annullata" || !(form.docente_id || sedeChk) || !form.data || !form.ora_inizio || !form.ora_fine || form.ora_fine <= form.ora_inizio) { setSessConf([]); return; }
    let active = true;
    const t = setTimeout(() => {
      api.get("/sessioni/conflitti", { params: { ...(form.docente_id ? { docente_id: form.docente_id } : {}), ...(sedeChk ? { sede: sedeChk } : {}), data_inizio: `${form.data}T${form.ora_inizio}`, data_fine: `${form.data}T${form.ora_fine}` } })
        .then((r) => { if (active) setSessConf(r.data); }).catch(() => { if (active) setSessConf([]); });
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [dialog, form.docente_id, form.data, form.ora_inizio, form.ora_fine, form.stato, form.sede, form.modalita]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/corso/${id}/full`);
      setData(data);
    } catch (e) {
      toast.error("Corso non trovato");
      navigate("/corsi");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/persone").then((r) => setPersone(r.data));
    api.get("/docenti").then((r) => setDocenti(r.data));
    api.get("/aziende").then((r) => setAziende(r.data));
    api.get("/personale").then((r) => setPersonale(r.data));
    api.get("/options/programma_specifico").then((r) => setOpts((o) => ({ ...o, programma_specifico: r.data.values })));
  }, []);

  const reloadPersone = () => api.get("/persone").then((r) => setPersone(r.data));
  const reloadDocenti = () => api.get("/docenti").then((r) => setDocenti(r.data));
  const reloadAziende = () => api.get("/aziende").then((r) => setAziende(r.data));

  const openQc = (type) => {
    if (type === "persona") setQc({ type, form: { nome: "", cognome: "", cf: "", email: "", azienda_id: "" } });
    else if (type === "docente") setQc({ type, form: { nome: "", cognome: "", cf: "", email: "", ruoli: [dialog === "incarico" && form.ruolo === "tutor" ? "tutor" : "docente"] } });
    else if (type === "azienda") setQc({ type, form: { denominazione: "", piva: "" } });
    else if (type === "personale") setQc({ type, form: { nome: "", cognome: "", ruolo: "" } });
  };

  const pickRisorsa = (r) => setForm((f) => (f.categoria !== undefined && dialog === "costo"
    ? { ...f, fornitore: r.label, risorsa_id: r.id, risorsa_tipo: r.tipo }
    : r.tipo === "docente" ? { ...f, docente_id: r.id } : r.tipo === "personale" ? { ...f, personale_id: r.id } : { ...f, committente_id: r.id }));

  const submitQc = async () => {
    setSaving(true);
    try {
      if (qc.type === "persona") {
        const { data: p } = await api.post("/persone", qc.form);
        await reloadPersone();
        setForm((f) => ({ ...f, persona_id: p.id, azienda_id: qc.form.azienda_id || f.azienda_id }));
      } else if (qc.type === "docente") {
        const { data: d } = await api.post("/docenti", qc.form);
        await reloadDocenti();
        pickRisorsa({ id: d.id, tipo: "docente", label: `${d.cognome} ${d.nome}` });
      } else if (qc.type === "personale") {
        const { data: d } = await api.post("/personale", qc.form);
        await api.get("/personale").then((r) => setPersonale(r.data));
        pickRisorsa({ id: d.id, tipo: "personale", label: `${d.cognome} ${d.nome}` });
      } else if (qc.type === "azienda") {
        const { data: a } = await api.post("/aziende", qc.form);
        await reloadAziende();
        pickRisorsa({ id: a.id, tipo: "azienda", label: a.denominazione });
      }
      toast.success("Anagrafica creata");
      setQc(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeStato = async (stato) => {
    try {
      await api.post(`/corso/${id}/stato`, { stato });
      toast.success("Stato aggiornato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDialog = (type) => {
    const c = data.corso;
    if (type === "iscrizione") setForm({ persona_id: "", azienda_id: "", stato: "confermato", ore_frequentate: 0, sede_id: "" });
    else if (type === "incarico") setForm({ risorsa_tipo: "docente", docente_id: "", personale_id: "", ruolo: "docente", ore_previste: c.ore_previste || 0, ore_effettive: 0, tariffa_applicata: 0, sede_id: "" });
    else if (type === "sessione") setForm({ data: "", ora_inizio: "09:00", ora_fine: "13:00", sede: "", modalita: "aula", argomento: "", stato: "pianificata", docente_id: "" });
    else if (type === "documento") { setForm({ titolo: "", categoria: "modulo" }); setFile(null); }
    else if (type === "corso") setForm({ titolo: c.titolo || "", ore_previste: c.ore_previste ?? "", responsabile: c.responsabile || "", data_avvio: (c.data_avvio || "").slice(0, 10), data_termine: (c.data_termine || "").slice(0, 10), note: c.note || "" });
    else if (type === "sede") setForm({ nome: "", indirizzo: "", citta: "", aula: "", capienza: "", note: "" });
    else if (type === "finanziamento") {
      let scad = c.data_scadenza_rendiconto || "";
      if (!scad && c.data_termine) scad = plus60(c.data_termine);
      setForm({ ...(c.finanziamento_dettagli || {}), data_scadenza_rendiconto: scad });
    }
    else if (type === "costo") setForm({ categoria: "fattura_ricevuta", tipo: "consuntivo", importo: "", descrizione: "", fornitore: "", data_competenza: "", stato_verifica: "da_verificare" });
    setDialog(type);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (dialog === "corso") {
        const c = data.corso;
        const payload = {
          titolo: form.titolo, ore_previste: parseFloat(form.ore_previste) || 0,
          responsabile: form.responsabile, data_avvio: form.data_avvio || null,
          data_termine: form.data_termine || null, note: form.note,
        };
        const oldTerm = (c.data_termine || "").slice(0, 10);
        if (form.data_termine && form.data_termine !== oldTerm && (!c.data_scadenza_rendiconto || c.data_scadenza_rendiconto === plus60(oldTerm))) {
          payload.data_scadenza_rendiconto = plus60(form.data_termine);
        }
        await api.put(`/corsi/${id}`, payload);
      } else if (dialog === "sede") {
        await api.post("/sedi", {
          corso_id: id, nome: form.nome, indirizzo: form.indirizzo, citta: form.citta,
          aula: form.aula, capienza: parseInt(form.capienza) || null, note: form.note,
        });
      } else if (dialog === "iscrizione") {
        const persona = persone.find((p) => p.id === form.persona_id);
        await api.post("/iscrizioni", {
          corso_id: id, persona_id: form.persona_id,
          azienda_id: form.azienda_id || persona?.azienda_id || null,
          stato: form.stato, ore_frequentate: parseFloat(form.ore_frequentate) || 0,
          sede_id: form.sede_id || null,
          data_iscrizione: new Date().toISOString().slice(0, 10),
        });
      } else if (dialog === "incarico") {
        await api.post("/incarichi", {
          corso_id: id, risorsa_tipo: form.risorsa_tipo, ruolo: form.ruolo,
          docente_id: form.risorsa_tipo === "docente" ? form.docente_id : null,
          personale_id: form.risorsa_tipo === "personale" ? form.personale_id : null,
          ore_previste: parseFloat(form.ore_previste) || 0,
          ore_effettive: parseFloat(form.ore_effettive) || 0,
          tariffa_applicata: parseFloat(form.tariffa_applicata) || 0,
          sede_id: form.sede_id || null,
        });
      } else if (dialog === "sessione") {
        if (form.ora_inizio && form.ora_fine && form.ora_fine <= form.ora_inizio) { toast.error("L'ora di fine deve essere successiva all'ora di inizio"); setSaving(false); return; }
        await api.post("/sessioni", {
          corso_id: id, data_inizio: `${form.data}T${form.ora_inizio}:00`,
          data_fine: `${form.data}T${form.ora_fine}:00`, sede: form.sede,
          modalita: form.modalita, argomento: form.argomento, stato: form.stato,
          docente_ids: form.docente_id ? [form.docente_id] : [],
        });
        if (sessConf.length) toast.warning("Lezione salvata con sovrapposizione: docente/tutor o aula già impegnati in quell'orario");
      } else if (dialog === "documento") {
        if (!file) { toast.error("Seleziona un file"); setSaving(false); return; }
        const fd = new FormData();
        fd.append("file", file); fd.append("titolo", form.titolo || file.name);
        fd.append("categoria", form.categoria); fd.append("corso_id", id);
        await api.post("/documenti/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else if (dialog === "finanziamento") {
        const det = { ...form };
        const scad = det.data_scadenza_rendiconto || null;
        delete det.data_scadenza_rendiconto;
        ["contributo", "finanziamento_approvato", "finanziamento_rendicontato", "valore_preventivo", "quota_partecipante", "importo_contratto_indar"].forEach((k) => {
          if (det[k] !== undefined && det[k] !== "") det[k] = parseFloat(det[k]);
        });
        for (const f of finFieldsOf(data.corso).filter((x) => x.type === "combobox")) {
          const v = (det[f.name] || "").trim();
          if (v && !(opts[f.optionKey] || []).includes(v)) {
            const { data: o } = await api.post(`/options/${f.optionKey}`, { value: v });
            setOpts((p) => ({ ...p, [f.optionKey]: o.values }));
          }
        }
        await api.put(`/corsi/${id}`, { finanziamento_dettagli: det, data_scadenza_rendiconto: scad });
      } else if (dialog === "costo") {
        await api.post("/costi", {
          corso_id: id, categoria: form.categoria, tipo: form.tipo,
          importo: parseFloat(form.importo) || 0, descrizione: form.descrizione,
          fornitore: form.fornitore, risorsa_id: form.risorsa_id || null, risorsa_tipo: form.risorsa_tipo || null,
          data_competenza: form.data_competenza,
          stato_verifica: form.stato_verifica,
        });
      }
      toast.success("Salvato");
      setDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delRow = async (coll, rid) => {
    if (!window.confirm("Rimuovere questo elemento?")) return;
    await api.delete(`/${coll}/${rid}`);
    toast.success("Rimosso");
    load();
  };

  const changeStatoIsc = async (isc, stato) => {
    try {
      await api.put(`/iscrizioni/${isc.id}`, { stato });
      toast.success("Stato iscrizione aggiornato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const savePresenza = async (isc, ore) => {
    await api.put(`/iscrizioni/${isc.id}`, { ore_frequentate: parseFloat(ore) || 0 });
    load();
  };

  if (!data)
    return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const c = data.corso;
  const calc = data.calc;
  const st = STATO_CORSO[c.stato] || STATO_CORSO.bozza;
  const tp = MACRO_TIPOLOGIA[c.macro_tipologia] || {};
  const canEcon = user?.can_economics;
  const canWrite = user?.can_write;
  const finFields = (FIN_FIELDS[c.macro_tipologia] || []).filter((f) => canEcon || !FIN_ECON.includes(f.name));
  const det = c.finanziamento_dettagli || {};

  const time = (s) => { try { return new Date(s).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div data-testid="corso-detail-page">
      <button onClick={() => navigate("/corsi")} className="mb-4 flex min-h-9 items-center gap-1.5 py-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Torna ai corsi
      </button>

      {/* Header */}
      <div className="rounded-xl border bg-white p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-[#E11D48]">{c.codice}</span>
              <Badge label={tp.label || c.macro_tipologia} cls={tp.cls || ""} />
            </div>
            <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">{c.titolo}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {c.ore_previste}h previste</span>
              <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {fmtDate(c.data_avvio)} → {fmtDate(c.data_termine)}</span>
              <span>Resp.: {c.responsabile || "—"}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge label={st.label} cls={`${st.cls} whitespace-nowrap`} />
            {canWrite && (
              <Button variant="outline" size="sm" onClick={() => openDialog("corso")} data-testid="edit-corso-btn">
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifica
              </Button>
            )}
            {canWrite && (
              <Select value={c.stato} onValueChange={changeStato}>
                <SelectTrigger className="w-full sm:w-48" data-testid="stato-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATO_CORSO).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat icon={Users} label="Partecipanti" value={calc.n_partecipanti} />
          <Stat icon={Presentation} label="Personale" value={calc.n_docenti} />
          <Stat icon={CalendarDays} label="Sessioni" value={calc.n_sessioni} />
          <Stat icon={Clock} label="Ore erogate" value={`${calc.ore_effettive}h`} />
          {canEcon ? (
            <>
              <Stat icon={Euro} label="Preventivo" value={fmtEuro(calc.preventivo_totale)} hint={`da: ${calc.preventivo_fonte}`} />
              <Stat icon={Euro} label="Consuntivo" value={fmtEuro(calc.consuntivo_totale)} />
            </>
          ) : (
            <div className="rounded-lg border border-dashed bg-slate-50 p-3 col-span-2 flex items-center gap-2 text-xs text-slate-400">
              <Lock className="h-4 w-4" /> Dati economici riservati
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="riepilogo" className="mt-5">
        <TabsList className="flex w-full justify-start flex-nowrap overflow-x-auto h-auto [&>button]:shrink-0">
          <TabsTrigger value="riepilogo" data-testid="tab-riepilogo"><ClipboardCheck className="mr-1.5 h-4 w-4" />Riepilogo</TabsTrigger>
          <TabsTrigger value="partecipanti" data-testid="tab-partecipanti"><Users className="mr-1.5 h-4 w-4" />Partecipanti</TabsTrigger>
          <TabsTrigger value="docenti" data-testid="tab-docenti"><Presentation className="mr-1.5 h-4 w-4" />Personale</TabsTrigger>
          <TabsTrigger value="calendario" data-testid="tab-calendario"><CalendarDays className="mr-1.5 h-4 w-4" />Calendario</TabsTrigger>
          <TabsTrigger value="sedi" data-testid="tab-sedi"><MapPin className="mr-1.5 h-4 w-4" />Sedi</TabsTrigger>
          <TabsTrigger value="presenze" data-testid="tab-presenze"><ClipboardCheck className="mr-1.5 h-4 w-4" />Presenze</TabsTrigger>
          <TabsTrigger value="documenti" data-testid="tab-documenti"><FolderArchive className="mr-1.5 h-4 w-4" />Documenti</TabsTrigger>
          {canEcon && <TabsTrigger value="costi" data-testid="tab-costi"><Euro className="mr-1.5 h-4 w-4" />Costi / Rendiconto</TabsTrigger>}
          <TabsTrigger value="attivita" data-testid="tab-attivita"><ListChecks className="mr-1.5 h-4 w-4" />Attività</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico"><History className="mr-1.5 h-4 w-4" />Storico</TabsTrigger>
        </TabsList>

        {/* Riepilogo */}
        <TabsContent value="riepilogo" className="mt-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-xl border bg-white p-5 lg:col-span-2">
              <h3 className="font-display text-lg font-semibold">Informazioni Generali</h3>
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 text-sm">
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Codice</dt><dd className="font-mono font-medium">{c.codice}</dd></div>
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Tipologia</dt><dd>{tp.label}</dd></div>
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Codice Progetto</dt><dd className="font-mono" data-testid="riepilogo-codice-progetto">{det.codice_progetto || "—"}</dd></div>
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Ore previste</dt><dd>{c.ore_previste}h</dd></div>
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Ore erogate</dt><dd>{calc.ore_effettive}h</dd></div>
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Avvio</dt><dd>{fmtDate(c.data_avvio)}</dd></div>
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Termine</dt><dd>{fmtDate(c.data_termine)}</dd></div>
                <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Responsabile</dt><dd>{c.responsabile || "—"}</dd></div>
              </dl>
              {c.note && <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{c.note}</p>}

              {/* Conditional funding details */}
              {finFields.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><Landmark className="h-5 w-5 text-[#E11D48]" />Dettagli Finanziamento / Pratica</h3>
                    {canWrite && (
                      <Button variant="outline" size="sm" onClick={() => openDialog("finanziamento")} data-testid="edit-finanziamento-btn">
                        <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifica
                      </Button>
                    )}
                  </div>
                  <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2 text-sm">
                    {finFields.map((f) => {
                      let val = det[f.name];
                      if (f.type === "date") val = val ? fmtDate(val) : "—";
                      else if (f.type === "number") val = val != null && val !== "" ? fmtEuro(val) : "—";
                      else val = val || "—";
                      return (
                        <div key={f.name} className="flex justify-between border-b border-slate-100 pb-2">
                          <dt className="text-slate-500">{f.label}</dt>
                          <dd className="font-medium text-right">{val}</dd>
                        </div>
                      );
                    })}
                    <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Committente</dt><dd className="font-medium text-right">{aziende.find((a) => a.id === det.committente_id)?.denominazione || "—"}</dd></div>
                    <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Scadenza rendiconto</dt><dd className="font-medium text-right">{c.data_scadenza_rendiconto ? fmtDate(c.data_scadenza_rendiconto) : "—"}</dd></div>
                  </dl>
                  {det.note && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{det.note}</p>}
                </div>
              )}
            </div>
            <div className="rounded-xl border bg-white p-5">
              <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><Building2 className="h-5 w-5 text-slate-400" />Aziende</h3>
              <div className="mt-3 space-y-2">
                {data.aziende.length === 0 && <p className="text-sm text-slate-400">Nessuna azienda collegata</p>}
                {data.aziende.map((a) => (
                  <Link key={a.id} to="/aziende" className="block rounded-lg border border-slate-100 px-3 py-2 text-sm font-medium text-slate-800 hover:border-[#FDA4AF]">
                    {a.denominazione}
                  </Link>
                ))}
              </div>
              {calc.ore_scostamento !== 0 && (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Le ore erogate ({calc.ore_effettive}h) differiscono dalle previste ({calc.ore_previste}h).
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Partecipanti */}
        <TabsContent value="partecipanti" className="mt-4">
          <TabHeader title="Iscritti" count={data.iscrizioni.length} onAdd={canWrite ? () => openDialog("iscrizione") : null} addLabel="Aggiungi iscritto" testid="add-iscrizione-btn" />
          <TableCard
            headers={["Partecipante", "Codice Fiscale", "Azienda", "Stato", "Ore Freq.", "Sede"]}
            rows={data.iscrizioni}
            empty="Nessun iscritto"
            actions={canWrite}
            onDelete={(r) => delRow("iscrizioni", r.id)}
            render={(i) => (
              <>
                <TableCell className="font-medium text-slate-900"><Link to={`/persone/${i.persona_id}`} className="hover:text-[#E11D48] hover:underline" data-testid="link-persona">{i.persona_nome}</Link></TableCell>
                <TableCell className="font-mono text-xs">{i.persona_cf}</TableCell>
                <TableCell className="text-sm" data-testid="isc-azienda">{i.azienda_nome || "—"}</TableCell>
                <TableCell>
                  {canWrite ? (
                    <Select value={i.stato} onValueChange={(v) => changeStatoIsc(i, v)}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid="isc-stato-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATO_ISCRIZIONE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <Badge label={STATO_ISCRIZIONE[i.stato] || i.stato} cls="bg-slate-100 text-slate-600 border-slate-200" />}
                </TableCell>
                <TableCell className="text-sm">{i.ore_frequentate ?? 0}h</TableCell>
                <TableCell className="text-sm">{i.sede_nome || "—"}</TableCell>
              </>
            )}
          />
        </TabsContent>

        {/* Docenti */}
        <TabsContent value="docenti" className="mt-4">
          <TabHeader title="Personale del corso" count={data.incarichi.length} onAdd={canWrite ? () => openDialog("incarico") : null} addLabel="Assegna personale" testid="add-incarico-btn" />
          <TableCard
            headers={canEcon ? ["Nominativo", "Ruolo", "Origine", "Ore prev.", "Ore eff.", "Tariffa", "Costo", "Sede"] : ["Nominativo", "Ruolo", "Origine", "Ore prev.", "Ore eff.", "Sede"]}
            rows={data.incarichi}
            empty="Nessun incarico"
            actions={canWrite}
            onDelete={(r) => delRow("incarichi", r.id)}
            render={(inc) => (
              <>
                <TableCell className="font-medium text-slate-900"><Link to={inc.risorsa_tipo === "personale" ? "/personale" : `/docenti/${inc.docente_id}`} className="hover:text-[#E11D48] hover:underline" data-testid="link-docente">{inc.docente_nome}</Link></TableCell>
                <TableCell className="text-sm" data-testid="inc-ruolo">{RUOLI.find((r) => r.value === inc.ruolo)?.label || inc.ruolo}</TableCell>
                <TableCell className="text-sm" data-testid="inc-origine">
                  <span className="flex items-center gap-1.5">{inc.risorsa_tipo === "personale" ? "Personale INDAR" : "Docente"}
                    {inc.risorsa_tipo === "personale" && contrattoWarn(personale.find((p) => p.id === inc.personale_id), c) && (
                      <span title={contrattoWarn(personale.find((p) => p.id === inc.personale_id), c)} data-testid="inc-contratto-warning"><AlertTriangle className="h-4 w-4 text-amber-500" /></span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{inc.ore_previste}h</TableCell>
                <TableCell className="text-sm">{inc.ore_effettive}h</TableCell>
                {canEcon && <TableCell className="text-sm">{fmtEuro(inc.tariffa_applicata)}</TableCell>}
                {canEcon && <TableCell className="font-semibold">{fmtEuro(inc.costo)}</TableCell>}
                <TableCell className="text-sm">{inc.sede_nome || "—"}</TableCell>
              </>
            )}
          />
        </TabsContent>

        {/* Calendario */}
        <TabsContent value="calendario" className="mt-4">
          <TabHeader title="Sessioni" count={data.sessioni.length} onAdd={canWrite ? () => openDialog("sessione") : null} addLabel="Nuova sessione" testid="add-sessione-btn" />
          <TableCard
            headers={["Data", "Orario", "Ore", "Argomento", "Sede", "Docenti", "Stato"]}
            rows={data.sessioni}
            empty="Nessuna sessione"
            actions={canWrite}
            onDelete={(r) => delRow("sessioni", r.id)}
            render={(s) => (
              <>
                <TableCell className="text-sm">{fmtDate(s.data_inizio)}</TableCell>
                <TableCell className="font-mono text-xs">{time(s.data_inizio)}–{time(s.data_fine)}</TableCell>
                <TableCell className="text-sm">{s.ore}h</TableCell>
                <TableCell className="text-sm">{s.argomento}</TableCell>
                <TableCell className="text-sm">{s.sede} ({s.modalita})</TableCell>
                <TableCell className="text-sm">{(s.docenti_nomi || []).join(", ")}</TableCell>
                <TableCell><Badge label={s.stato} cls="bg-slate-100 text-slate-600 border-slate-200" /></TableCell>
              </>
            )}
          />
        </TabsContent>

        {/* Sedi */}
        <TabsContent value="sedi" className="mt-4">
          <TabHeader title="Sedi del corso" count={(data.sedi || []).length} onAdd={canWrite ? () => openDialog("sede") : null} addLabel="Aggiungi sede" testid="add-sede-btn" />
          <TableCard
            headers={["Nome", "Indirizzo", "Città", "Aula", "Capienza"]}
            rows={data.sedi || []}
            empty="Nessuna sede registrata"
            actions={canWrite}
            onDelete={(r) => delRow("sedi", r.id)}
            render={(s) => (
              <>
                <TableCell className="font-medium text-slate-900">{s.nome}</TableCell>
                <TableCell className="text-sm">{s.indirizzo || "—"}</TableCell>
                <TableCell className="text-sm">{s.citta || "—"}</TableCell>
                <TableCell className="text-sm">{s.aula || "—"}</TableCell>
                <TableCell className="text-sm">{s.capienza || "—"}</TableCell>
              </>
            )}
          />
        </TabsContent>

        {/* Presenze */}
        <TabsContent value="presenze" className="mt-4">
          <TableCard
            headers={["Partecipante", "Ore Frequentate", "Ore Corso", "Frequenza %"]}
            rows={data.iscrizioni}
            empty="Nessun partecipante"
            render={(i) => {
              const perc = c.ore_previste ? Math.round(((i.ore_frequentate || 0) / c.ore_previste) * 100) : 0;
              return (
                <>
                  <TableCell className="font-medium text-slate-900">{i.persona_nome}</TableCell>
                  <TableCell>
                    {canWrite ? (
                      <Input type="number" defaultValue={i.ore_frequentate ?? 0} className="h-8 w-24"
                        onBlur={(e) => { if (parseFloat(e.target.value) !== (i.ore_frequentate ?? 0)) savePresenza(i, e.target.value); }}
                        data-testid="presenza-input" />
                    ) : <span className="text-sm">{i.ore_frequentate ?? 0}h</span>}
                  </TableCell>
                  <TableCell className="text-sm">{c.ore_previste}h</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full ${perc >= 70 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.min(perc, 100)}%` }} />
                      </div>
                      <span className="text-sm font-medium">{perc}%</span>
                    </div>
                  </TableCell>
                </>
              );
            }}
          />
        </TabsContent>

        {/* Documenti */}
        <TabsContent value="documenti" className="mt-4">
          <TabHeader title="Documenti del corso" count={data.documenti.length} onAdd={canWrite ? () => openDialog("documento") : null} addLabel="Carica documento" testid="add-documento-btn" addIcon={Upload} />
          <TableCard
            headers={["Titolo", "Categoria", "File", "Versione", "Data"]}
            rows={data.documenti}
            empty="Nessun documento"
            actions={true}
            onPreview={(r) => r.storage_path && previewDocument(r.id)}
            onDelete={canWrite ? (r) => delRow("documenti", r.id) : null}
            render={(d) => (
              <>
                <TableCell className="font-medium text-slate-900">{d.titolo}</TableCell>
                <TableCell><Badge label={d.categoria} cls="bg-slate-100 text-slate-600 border-slate-200" /></TableCell>
                <TableCell className="text-sm text-slate-500">{d.original_filename || "—"}</TableCell>
                <TableCell className="font-mono text-xs">v{d.versione || 1}</TableCell>
                <TableCell className="text-sm">{fmtDate(d.data_documento)}</TableCell>
              </>
            )}
          />
        </TabsContent>

        {/* Costi */}
        {canEcon && (
          <TabsContent value="costi" className="mt-4">
            {c.macro_tipologia === "fondi_interprofessionali" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-4">
                <div className="rounded-xl border bg-white p-4"><div className="text-xs uppercase text-slate-500">Finanziamento Approvato</div><div className="mt-1 font-display text-xl font-bold">{fmtEuro(det.finanziamento_approvato || 0)}</div></div>
                <div className="rounded-xl border bg-white p-4"><div className="text-xs uppercase text-slate-500">Rendicontato (consuntivo)</div><div className="mt-1 font-display text-xl font-bold">{fmtEuro(calc.consuntivo_totale || 0)}</div></div>
                <div className="rounded-xl border bg-white p-4"><div className="text-xs uppercase text-slate-500">Residuo da rendicontare</div><div className="mt-1 font-display text-xl font-bold text-slate-900">{fmtEuro((det.finanziamento_approvato || 0) - (calc.consuntivo_totale || 0))}</div></div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-4">
              <div className="rounded-xl border bg-white p-4"><div className="text-xs uppercase text-slate-500">Preventivo</div><div className="mt-1 font-display text-xl font-bold" data-testid="costi-preventivo">{fmtEuro(calc.preventivo_totale)}</div><div className="mt-0.5 text-xs text-slate-400" data-testid="preventivo-fonte">da: {calc.preventivo_fonte}{calc.preventivo_fonte !== "Somma voci di preventivo" && calc.preventivo_voci ? ` · voci di preventivo ${fmtEuro(calc.preventivo_voci)}` : ""}</div></div>
              <div className="rounded-xl border bg-white p-4"><div className="text-xs uppercase text-slate-500">Consuntivo</div><div className="mt-1 font-display text-xl font-bold">{fmtEuro(calc.consuntivo_totale)}</div></div>
              <div className="rounded-xl border bg-white p-4"><div className="text-xs uppercase text-slate-500">Scostamento</div><div className={`mt-1 font-display text-xl font-bold ${calc.scostamento > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtEuro(calc.scostamento)}</div></div>
            </div>
            <TabHeader title="Voci di rendiconto / costi" count={data.costi.length} onAdd={canWrite ? () => openDialog("costo") : null} addLabel="Aggiungi voce" testid="add-costo-btn" />
            <TableCard
              headers={["Categoria", "Fase", "Descrizione", "Fornitore/Risorsa", "Importo", "Verifica"]}
              rows={data.costi}
              empty="Nessuna voce di rendiconto registrata"
              actions={canWrite}
              onDelete={(r) => delRow("costi", r.id)}
              render={(co) => (
                <>
                  <TableCell className="capitalize text-sm font-medium">{(co.categoria || "").replace(/_/g, " ")}</TableCell>
                  <TableCell><Badge label={co.tipo} cls={co.tipo === "consuntivo" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-600 border-slate-200"} /></TableCell>
                  <TableCell className="text-sm">{co.descrizione}</TableCell>
                  <TableCell className="text-sm">{co.fornitore || "—"}</TableCell>
                  <TableCell className="font-semibold">{fmtEuro(co.importo)}</TableCell>
                  <TableCell className="text-sm capitalize">{(co.stato_verifica || "").replace(/_/g, " ")}</TableCell>
                </>
              )}
            />
          </TabsContent>
        )}

        {/* Attività */}
        <TabsContent value="attivita" className="mt-4">
          <TableCard
            headers={["Titolo", "Assegnatario", "Priorità", "Scadenza", "Stato"]}
            rows={data.attivita}
            empty="Nessuna attività"
            render={(a) => {
              const p = PRIORITA[a.priorita] || PRIORITA.normale;
              return (
                <>
                  <TableCell className="font-medium text-slate-900">{a.titolo}</TableCell>
                  <TableCell className="text-sm">{a.assegnatario}</TableCell>
                  <TableCell><Badge label={p.label} cls={p.cls + " border-transparent"} /></TableCell>
                  <TableCell className="text-sm">{fmtDate(a.scadenza)}</TableCell>
                  <TableCell className="text-sm">{STATO_TASK[a.stato] || a.stato}</TableCell>
                </>
              );
            }}
          />
        </TabsContent>

        {/* Storico */}
        <TabsContent value="storico" className="mt-4">
          <div className="rounded-xl border bg-white p-5">
            <div className="space-y-4">
              {data.history.length === 0 && <p className="text-sm text-slate-400">Nessuna operazione registrata.</p>}
              {data.history.map((h) => (
                <div key={h.id} className="flex gap-3" data-testid="history-item">
                  <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#E11D48]" />
                  <div>
                    <div className="text-sm font-medium text-slate-900">{h.action}</div>
                    <div className="text-sm text-slate-500">{h.detail}</div>
                    <div className="mt-0.5 text-xs text-slate-400">{fmtDateTime(h.timestamp)} · {h.user_email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialog === "iscrizione" && "Aggiungi Iscritto"}
              {dialog === "incarico" && "Assegna Personale"}
              {dialog === "sessione" && "Nuova Sessione"}
              {dialog === "documento" && "Carica Documento"}
              {dialog === "corso" && "Modifica Corso"}
              {dialog === "sede" && "Nuova Sede"}
              {dialog === "finanziamento" && "Dettagli Finanziamento / Pratica"}
              {dialog === "costo" && "Nuova voce di rendiconto / costo"}
            </DialogTitle>
            <DialogDescription>Compila i campi e salva per aggiornare il corso.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {dialog === "iscrizione" && <>
              <Field label="Partecipante" full>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select value={form.persona_id} onValueChange={(v) => { const p = persone.find((x) => x.id === v); setForm({ ...form, persona_id: v, azienda_id: p?.azienda_id || "" }); }}>
                      <SelectTrigger data-testid="sel-persona"><SelectValue placeholder="Seleziona persona" /></SelectTrigger>
                      <SelectContent>{persone.map((p) => <SelectItem key={p.id} value={p.id}>{p.cognome} {p.nome} — {p.cf}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {canWrite && <Button type="button" variant="outline" size="icon" onClick={() => openQc("persona")} data-testid="qc-persona-btn" title="Crea nuova persona"><UserPlus className="h-4 w-4" /></Button>}
                </div>
              </Field>
              <Field label="Stato">
                <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                  <SelectTrigger data-testid="sel-stato-isc"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATO_ISCRIZIONE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Ore frequentate"><Input type="number" value={form.ore_frequentate} onChange={(e) => setForm({ ...form, ore_frequentate: e.target.value })} data-testid="inp-ore-freq" /></Field>
              {(data.sedi || []).length > 0 && <Field label="Sede">
                <Select value={form.sede_id || ""} onValueChange={(v) => setForm({ ...form, sede_id: v })}>
                  <SelectTrigger data-testid="sel-sede-isc"><SelectValue placeholder="Opzionale" /></SelectTrigger>
                  <SelectContent>{data.sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                </Select>
              </Field>}
            </>}

            {dialog === "incarico" && <>
              <Field label="Ruolo">
                <Select value={form.ruolo} onValueChange={(v) => setForm({ ...form, ruolo: v })}>
                  <SelectTrigger data-testid="sel-ruolo"><SelectValue /></SelectTrigger>
                  <SelectContent>{RUOLI.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Origine">
                <Select value={form.risorsa_tipo} onValueChange={(v) => setForm({ ...form, risorsa_tipo: v, docente_id: "", personale_id: "" })}>
                  <SelectTrigger data-testid="sel-origine"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="docente">Docenti e tutor</SelectItem><SelectItem value="personale">Personale INDAR</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label={form.risorsa_tipo === "personale" ? "Personale INDAR" : "Docente / Tutor"} full>
                <div className="flex gap-2">
                  <div className="flex-1">
                    {form.risorsa_tipo === "personale" ? (
                      <Select value={form.personale_id} onValueChange={(v) => { const p = personale.find((x) => x.id === v); setForm({ ...form, personale_id: v, tariffa_applicata: p?.costo_orario ?? form.tariffa_applicata }); }}>
                        <SelectTrigger data-testid="sel-personale"><SelectValue placeholder="Seleziona persona" /></SelectTrigger>
                        <SelectContent>{personale.map((p) => <SelectItem key={p.id} value={p.id}>{p.cognome} {p.nome}{p.ruolo ? ` — ${p.ruolo}` : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Select value={form.docente_id} onValueChange={(v) => { const d = docenti.find((x) => x.id === v); setForm({ ...form, docente_id: v, tariffa_applicata: d?.tariffa_oraria ?? form.tariffa_applicata }); }}>
                        <SelectTrigger data-testid="sel-docente"><SelectValue placeholder="Seleziona docente" /></SelectTrigger>
                        <SelectContent>{docenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.cognome} {d.nome}{d.ruoli?.includes("tutor") ? (d.ruoli.includes("docente") ? " — Docente e Tutor" : " — Tutor") : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                  {canWrite && <Button type="button" variant="outline" size="icon" onClick={() => openQc(form.risorsa_tipo)} data-testid="qc-docente-btn" title="Crea nuova anagrafica"><UserPlus className="h-4 w-4" /></Button>}
                </div>
              </Field>
              {form.risorsa_tipo === "personale" && contrattoWarn(personale.find((p) => p.id === form.personale_id), c) && (
                <div className="sm:col-span-2 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="inc-contratto-alert">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {contrattoWarn(personale.find((p) => p.id === form.personale_id), c)}
                </div>
              )}
              <Field label="Ore previste"><Input type="number" value={form.ore_previste} onChange={(e) => setForm({ ...form, ore_previste: e.target.value })} data-testid="inp-ore-prev" /></Field>
              <Field label="Ore effettive"><Input type="number" value={form.ore_effettive} onChange={(e) => setForm({ ...form, ore_effettive: e.target.value })} data-testid="inp-ore-eff" /></Field>
              {canEcon && <Field label="Tariffa applicata (€)"><Input type="number" value={form.tariffa_applicata} onChange={(e) => setForm({ ...form, tariffa_applicata: e.target.value })} data-testid="inp-tariffa" /></Field>}
              {(data.sedi || []).length > 0 && <Field label="Sede">
                <Select value={form.sede_id || ""} onValueChange={(v) => setForm({ ...form, sede_id: v })}>
                  <SelectTrigger data-testid="sel-sede-inc"><SelectValue placeholder="Opzionale" /></SelectTrigger>
                  <SelectContent>{data.sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                </Select>
              </Field>}
            </>}

            {dialog === "sessione" && <>
              <Field label="Data"><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="inp-data" /></Field>
              <Field label="Argomento"><Input value={form.argomento} onChange={(e) => setForm({ ...form, argomento: e.target.value })} data-testid="inp-argomento" /></Field>
              <Field label="Ora inizio"><Input type="time" value={form.ora_inizio} onChange={(e) => setForm({ ...form, ora_inizio: e.target.value })} data-testid="inp-ora-inizio" /></Field>
              <Field label="Ora fine"><Input type="time" value={form.ora_fine} onChange={(e) => setForm({ ...form, ora_fine: e.target.value })} data-testid="inp-ora-fine" /></Field>
              <Field label="Sede">
                {(data.sedi || []).length ? (
                  <Select value={form.sede} onValueChange={(v) => setForm({ ...form, sede: v })}>
                    <SelectTrigger data-testid="sel-sede-sess"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                    <SelectContent>{data.sedi.map((s) => <SelectItem key={s.id} value={s.nome}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={form.sede} onChange={(e) => setForm({ ...form, sede: e.target.value })} data-testid="inp-sede" />
                )}
              </Field>
              <Field label="Modalità">
                <Select value={form.modalita} onValueChange={(v) => setForm({ ...form, modalita: v })}>
                  <SelectTrigger data-testid="sel-modalita"><SelectValue /></SelectTrigger>
                  <SelectContent>{["aula", "online", "ibrida"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Docente">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select value={form.docente_id} onValueChange={(v) => setForm({ ...form, docente_id: v })}>
                      <SelectTrigger data-testid="sel-docente-sess"><SelectValue placeholder="Opzionale" /></SelectTrigger>
                      <SelectContent>{docenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.cognome} {d.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {canWrite && <Button type="button" variant="outline" size="icon" onClick={() => openQc("docente")} data-testid="qc-docente-sess-btn" title="Crea nuovo docente"><UserPlus className="h-4 w-4" /></Button>}
                </div>
              </Field>
              {sessConf.length > 0 && (
                <div className="sm:col-span-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="sess-conflict-warning">
                  <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Sovrapposizione in questo orario</div>
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {sessConf.map((c) => (
                      <li key={`${c.session_id}-${c.tipo}`} data-testid={`sess-conflict-${c.tipo}`}><span className="font-semibold">{c.tipo === "aula" ? `Aula "${c.risorsa}" occupata` : "Docente/tutor già impegnato"}</span> — {c.corso_codice} · {c.corso_titolo}{c.argomento ? ` (${c.argomento})` : ""}: {fmtDate(c.data_inizio)} {time(c.data_inizio)}–{time(c.data_fine)}</li>
                    ))}
                  </ul>
                  <p className="mt-1 text-xs text-rose-600">Puoi salvare comunque, la lezione verrà segnalata nel calendario.</p>
                </div>
              )}
              <Field label="Stato">
                <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                  <SelectTrigger data-testid="sel-stato-sess"><SelectValue /></SelectTrigger>
                  <SelectContent>{["pianificata", "confermata", "svolta", "annullata", "rinviata"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </>}

            {dialog === "documento" && <>
              <Field label="Titolo" full><Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="inp-doc-titolo" /></Field>
              <Field label="Categoria">
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger data-testid="sel-doc-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{["contratto", "identita", "cv", "attestato", "fattura", "registro", "decreto", "rendiconto", "modulo", "altro"].map((cc) => <SelectItem key={cc} value={cc}>{cc}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-6 hover:border-[#FDA4AF]" data-testid="doc-dropzone-corso">
                  <UploadCloud className="h-7 w-7 text-slate-400" />
                  <span className="mt-2 text-sm font-medium text-slate-700">{file ? file.name : "Seleziona un file"}</span>
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="corso-doc-file" />
                </label>
              </div>
            </>}

            {dialog === "costo" && <>
              <Field label="Categoria">
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v, fornitore: "", risorsa_id: null, risorsa_tipo: null })}>
                  <SelectTrigger data-testid="sel-costo-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(RISORSE_CAT).map((cc) => <SelectItem key={cc} value={cc}>{cc.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Fase">
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="sel-costo-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{["preventivo", "consuntivo"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <RisorsaField form={form} setForm={setForm} canWrite={canWrite} openQc={openQc}
                options={risorseFor(form.categoria, { aziende, docenti, personale, sedi: data.sedi || [] })} />
              <Field label="Importo (€)"><Input type="number" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="inp-costo-importo" /></Field>
              <Field label="Data competenza"><Input type="date" value={form.data_competenza} onChange={(e) => setForm({ ...form, data_competenza: e.target.value })} data-testid="inp-costo-data" /></Field>
              <Field label="Descrizione" full><Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="inp-costo-desc" /></Field>
              <Field label="Stato verifica">
                <Select value={form.stato_verifica} onValueChange={(v) => setForm({ ...form, stato_verifica: v })}>
                  <SelectTrigger data-testid="sel-costo-verifica"><SelectValue /></SelectTrigger>
                  <SelectContent>{["da_verificare", "verificato", "non_ammissibile"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </>}

            {dialog === "corso" && <>
              <Field label="Titolo" full><Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="inp-corso-titolo" /></Field>
              <Field label="Ore previste"><Input type="number" value={form.ore_previste} onChange={(e) => setForm({ ...form, ore_previste: e.target.value })} data-testid="inp-corso-ore" /></Field>
              <Field label="Responsabile"><Input value={form.responsabile} onChange={(e) => setForm({ ...form, responsabile: e.target.value })} data-testid="inp-corso-resp" /></Field>
              <Field label="Data avvio"><Input type="date" value={form.data_avvio} onChange={(e) => setForm({ ...form, data_avvio: e.target.value })} data-testid="inp-corso-avvio" /></Field>
              <Field label="Data termine"><Input type="date" value={form.data_termine} onChange={(e) => setForm({ ...form, data_termine: e.target.value })} data-testid="inp-corso-termine" /></Field>
              <Field label="Note" full><Textarea rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="inp-corso-note" /></Field>
            </>}

            {dialog === "sede" && <>
              <Field label="Nome sede" full><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="inp-sede-nome" /></Field>
              <Field label="Indirizzo"><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} data-testid="inp-sede-indirizzo" /></Field>
              <Field label="Città"><Input value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} data-testid="inp-sede-citta" /></Field>
              <Field label="Aula"><Input value={form.aula} onChange={(e) => setForm({ ...form, aula: e.target.value })} data-testid="inp-sede-aula" /></Field>
              <Field label="Capienza"><Input type="number" value={form.capienza} onChange={(e) => setForm({ ...form, capienza: e.target.value })} data-testid="inp-sede-capienza" /></Field>
              <Field label="Note" full><Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="inp-sede-note" /></Field>
            </>}

            {dialog === "finanziamento" && <>
              <Field label="Committente" full>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select value={form.committente_id || ""} onValueChange={(v) => setForm({ ...form, committente_id: v })}>
                      <SelectTrigger data-testid="fin-committente"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                      <SelectContent>{aziende.map((a) => <SelectItem key={a.id} value={a.id}>{a.denominazione}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {canWrite && <Button type="button" variant="outline" size="icon" onClick={() => openQc("azienda")} data-testid="qc-azienda-btn" title="Crea nuova azienda"><Plus className="h-4 w-4" /></Button>}
                </div>
              </Field>
              <Field label="Data scadenza rendiconto">
                <Input type="date" value={form.data_scadenza_rendiconto || ""} onChange={(e) => setForm({ ...form, data_scadenza_rendiconto: e.target.value })} data-testid="fin-scadenza-rendiconto" />
                <p className="mt-1 text-xs text-slate-400">Calcolata automaticamente: data termine + 60 giorni</p>
              </Field>
            </>}

            {dialog === "finanziamento" && finFields.map((f) => (
              <Field key={f.name} label={f.label} full={f.type === "select"}>
                {f.type === "combobox" ? (
                  <>
                    <Input list={`opt-${f.name}`} value={form[f.name] || ""} placeholder="Scegli o scrivi un nuovo valore"
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`fin-${f.name}`} />
                    <datalist id={`opt-${f.name}`}>{(opts[f.optionKey] || []).map((o) => <option key={o} value={o} />)}</datalist>
                  </>
                ) : f.type === "select" ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger data-testid={`fin-${f.name}`}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                    value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`fin-${f.name}`} />
                )}
              </Field>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="dialog-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickCreateDialog qc={qc} setQc={setQc} aziende={aziende} saving={saving} onSubmit={submitQc} />
    </div>
  );
}

const plus60 = (iso) => { if (!iso) return ""; const d = new Date(`${iso.slice(0, 10)}T12:00:00`); d.setDate(d.getDate() + 60); return d.toISOString().slice(0, 10); };
const finFieldsOf = (c) => FIN_FIELDS[c.macro_tipologia] || [];
const FIN_ECON = ["contributo", "finanziamento_approvato", "finanziamento_rendicontato", "valore_preventivo", "quota_partecipante", "importo_contratto_indar"];

const itDate = (d) => d.slice(0, 10).split("-").reverse().join("/");

function contrattoWarn(p, c) {
  if (!p || !c) return "";
  const nome = `${p.cognome || ""} ${p.nome || ""}`.trim();
  if (p.cessato_il && c.data_avvio && p.cessato_il.slice(0, 10) < c.data_avvio.slice(0, 10))
    return `Il contratto di ${nome} è cessato il ${itDate(p.cessato_il)}, prima dell'avvio del corso (${itDate(c.data_avvio)}).`;
  if (p.cessato_il && c.data_termine && p.cessato_il.slice(0, 10) < c.data_termine.slice(0, 10))
    return `Il contratto di ${nome} cessa il ${itDate(p.cessato_il)}, prima della fine del corso (${itDate(c.data_termine)}).`;
  if (p.dal && c.data_avvio && p.dal.slice(0, 10) > c.data_avvio.slice(0, 10))
    return `${nome} risulta assunto il ${itDate(p.dal)}, dopo l'avvio del corso (${itDate(c.data_avvio)}).`;
  return "";
}

const RUOLI = [
  { value: "docente", label: "Docente" }, { value: "codocente", label: "Co-docente" },
  { value: "tutor", label: "Tutor" }, { value: "altro", label: "Altro" },
];

const RISORSE_CAT = {
  fattura_ricevuta: ["azienda"], materiale: ["azienda"], docenza: ["docente"], prestazione_occasionale: ["docente"],
  cedolino: ["personale"], personale_interno: ["personale"], aula: ["sede"], trasferta: ["docente", "personale"], altro: ["docente", "personale"],
};
const RISORSA_LBL = { azienda: "Aziende", docente: "Docenti", personale: "Personale INDAR", sede: "Sedi del corso" };
const RISORSA_ONE = { azienda: "Azienda", docente: "Docente", personale: "Personale INDAR", sede: "Sede" };

function risorseFor(cat, { aziende, docenti, personale, sedi }) {
  const src = {
    azienda: aziende.map((a) => ({ id: a.id, label: a.denominazione })),
    docente: docenti.map((d) => ({ id: d.id, label: `${d.cognome} ${d.nome}` })),
    personale: personale.map((p) => ({ id: p.id, label: `${p.cognome} ${p.nome}` })),
    sede: sedi.map((s) => ({ id: s.id, label: s.nome })),
  };
  return (RISORSE_CAT[cat] || []).flatMap((t) => src[t].map((o) => ({ ...o, tipo: t })));
}

function RisorsaField({ form, setForm, canWrite, openQc, options }) {
  const tipi = RISORSE_CAT[form.categoria] || [];
  const onType = (v) => {
    const m = options.find((o) => o.label.toLowerCase() === v.trim().toLowerCase());
    setForm({ ...form, fornitore: v, risorsa_id: m?.id || null, risorsa_tipo: m?.tipo || null });
  };
  return (
    <Field label={`Fornitore / Risorsa — ${tipi.map((t) => RISORSA_LBL[t]).join(" e ")}`} full>
      <div className="flex gap-2">
        <Input list="dl-risorse" value={form.fornitore || ""} onChange={(e) => onType(e.target.value)}
          placeholder="Scrivi per cercare o inserisci un nuovo nominativo" data-testid="inp-costo-fornitore" />
        <datalist id="dl-risorse">{options.map((o) => <option key={`${o.tipo}-${o.id}`} value={o.label}>{RISORSA_ONE[o.tipo]}</option>)}</datalist>
        {canWrite && tipi.filter((t) => t !== "sede").map((t) => (
          <Button key={t} type="button" variant="outline" size="sm" onClick={() => openQc(t)} data-testid={`qc-risorsa-${t}`} title={`Crea ${RISORSA_ONE[t]}`}>
            <Plus className="h-3.5 w-3.5" />{tipi.length > 1 ? <span className="ml-1 text-xs">{RISORSA_ONE[t]}</span> : null}
          </Button>
        ))}
      </div>
      <p className="mt-1 text-xs text-slate-400" data-testid="risorsa-stato">
        {form.risorsa_id ? `Collegato all'anagrafica ${RISORSA_ONE[form.risorsa_tipo]}` : form.fornitore ? "Testo libero (nessuna anagrafica collegata)" : `${options.length} voci disponibili`}
      </p>
    </Field>
  );
}

const QC_FIELDS = {
  persona: [["nome", "Nome"], ["cognome", "Cognome"], ["cf", "Codice fiscale"], ["email", "Email"]],
  docente: [["nome", "Nome"], ["cognome", "Cognome"], ["cf", "Codice fiscale"], ["email", "Email"]],
  azienda: [["denominazione", "Ragione sociale"], ["piva", "Partita IVA"]],
  personale: [["nome", "Nome"], ["cognome", "Cognome"], ["ruolo", "Ruolo"]],
};
const QC_TITLE = { persona: "Nuova persona", docente: "Nuovo docente", azienda: "Nuova azienda", personale: "Nuovo personale INDAR" };

function QuickCreateDialog({ qc, setQc, aziende, saving, onSubmit }) {
  const set = (k, v) => setQc({ ...qc, form: { ...qc.form, [k]: v } });
  return (
    <Dialog open={!!qc} onOpenChange={(o) => !o && setQc(null)}>
      <DialogContent className="max-w-lg" data-testid="qc-dialog">
        <DialogHeader>
          <DialogTitle>{qc && (qc.type === "docente" && qc.form.ruoli?.[0] === "tutor" ? "Nuovo tutor" : QC_TITLE[qc.type])}</DialogTitle>
          <DialogDescription>Creazione rapida anagrafica: sarà selezionata automaticamente.</DialogDescription>
        </DialogHeader>
        {qc && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {QC_FIELDS[qc.type].map(([k, l]) => (
              <Field key={k} label={l}><Input value={qc.form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`qc-${k}`} /></Field>
            ))}
            {qc.type === "persona" && (
              <Field label="Azienda" full>
                <Select value={qc.form.azienda_id || ""} onValueChange={(v) => set("azienda_id", v)}>
                  <SelectTrigger data-testid="qc-azienda"><SelectValue placeholder="Opzionale" /></SelectTrigger>
                  <SelectContent>{aziende.map((a) => <SelectItem key={a.id} value={a.id}>{a.denominazione}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setQc(null)}>Annulla</Button>
          <Button onClick={onSubmit} disabled={saving} className="bg-[#E11D48] hover:bg-[#BE123C]" data-testid="qc-save">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Crea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TabHeader({ title, count, onAdd, addLabel, testid, addIcon: AddIcon = Plus }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h3 className="font-display text-base font-semibold text-slate-800">{title} <span className="text-slate-400">({count})</span></h3>
      {onAdd && (
        <Button size="sm" onClick={onAdd} data-testid={testid} className="bg-[#E11D48] hover:bg-[#BE123C]">
          <AddIcon className="mr-1.5 h-4 w-4" /> {addLabel}
        </Button>
      )}
    </div>
  );
}

function TableCard({ headers, rows, render, empty, actions, onDelete, onPreview }) {
  if (!rows || rows.length === 0)
    return <div className="rounded-xl border border-dashed bg-white py-14 text-center text-sm text-slate-400">{empty}</div>;
  const showActions = actions || onDelete || onPreview;
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {headers.map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
              {showActions && <TableHead className="w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.id || i} data-testid="detail-row">
                {render(r)}
                {showActions && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {onPreview && r.storage_path && (
                        <button onClick={() => onPreview(r)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" data-testid="detail-preview"><Eye className="h-4 w-4" /></button>
                      )}
                      {onDelete && (
                        <button onClick={() => onDelete(r)} className="rounded p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600" data-testid="detail-delete"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
